import {BaseComponent} from '../base/component.base.js';
import template from './page-form.template.html';
import styles from './page-form.styles.css';
import {t, tpl} from '../../i18n/i18n.js';
import {displayName, pageUrlPath, liveUrl, toPublishFormData} from '../../core/page/page.core.js';
import {hasComposition} from '../../core/page/composition.core.js';
import {orderFormFields} from '../../core/page/form.core.js';
import {
    COMPOSITION_DATA_KEY,
    DEFAULT_LAYOUT_CONTEXT,
    FieldType,
    ImageCompanionSuffix,
    PageKey,
    RenderMode,
    isFileFieldType,
    isTruthyFlag,
} from '../../core/vocabulary.js';
import {SectionEditor} from './section-editor.js';
import {PreviewPanel} from './preview-panel.js';
import {LayoutPlacement} from './layout-placement.js';
import {imagePreparationsSettled, markFocalPoint, renderFieldGroup, syncFieldCounters} from './field-renderers.js';
import {bindSlugPreview, renderSlugPreview} from './slug-preview.js';
import {FieldReferenceControls} from './field-references.js';
import {showExistingAsset} from './existing-asset.js';
import {arrangeFieldSections, placeLayoutContextCardsInEditorGroups, renderEditorGroups, syncEditorGroupExpansion} from './editor-groups.js';
import '../table-field/table-field.component.js';

export class PageFormComponent extends BaseComponent {
    #templateName = '';
    #slugPrefix = null;
    #homepage = false;
    #domain = null;
    #layoutConfig = null;
    #templateContent = '';
    /** @type {RenderMode} */
    #mode = RenderMode.PAGE;
    #pageTitle = '';
    /** @type {import('../../shell/types/user.types.js').TemplateField[]} */
    #fields = [];
    #editMode = false;
    /** Whether the form differs from the state it was opened or last saved in. */
    #hasChanges = false;
    /** Set while the first invalid field of a save attempt is being revealed. */
    #revealedInvalid = false;
    /** Set while the browser checks the form for a save attempt (a click on a submit button). */
    #saveAttempt = false;
    /** Set while a save waits for the photos still being prepared; further saves are ignored. */
    #awaitingImages = false;
    #baselineSnapshot = null;
    #pagePath = '';
    /** False while editing a staged draft that has never been published. */
    #pageLive = true;
    /** True while editing a live page that has a staged edit not yet published. */
    #pagePending = false;
    #loadingMode = 'submit';
    #loading = false;
    #formListenerController = null;
    /** @type {import('../../shell/types/user.types.js').TemplateComposition | null} */
    #composition = null;
    /** @type {SectionEditor | null} */
    #sectionEditor = null;
    #fieldReferences = new FieldReferenceControls(this.shadowRoot);
    #preview = new PreviewPanel({
        root: this.shadowRoot,
        emit: (name, detail) => this.emit(name, detail),
        getFields: () => this.#fields,
        getMode: () => this.#mode,
        getTemplateName: () => this.#templateName,
    });
    #layout = new LayoutPlacement({
        root: this.shadowRoot,
        emit: (name, detail) => this.emit(name, detail),
        getState: () => ({
            fields: this.#fields,
            layoutConfig: this.#layoutConfig,
            pagePath: this.#pagePath,
            pageTitle: this.#pageTitle,
            editMode: this.#editMode,
            templateName: this.#templateName,
        }),
        onPositionChange: () => {
            this.#markChanged();
            this.#preview.scheduleRefresh();
            this.#evaluateFormReadiness();
        },
        onMapsRendered: () => this.#syncSubmitAvailability(),
    });

    #onLocaleChange = () => {
        const btn = this.$('#btn-submit');
        this.#syncHeading();
        if (btn && !this.#loading) this.#setSubmitButtonLabel(this.#editMode ? t('pageForm.actionStageEdit') : t('pageForm.actionStageDraft'));
        this.#syncStatusPill();
        this.#syncSubmitAvailability();
        this.#layout.renderMaps();
        this.#sectionEditor?.refresh();
        this.#updateSlugPreview();
    };

    connectedCallback() {
        this.loadTemplate(template, styles);
        document.addEventListener('locale-change', this.#onLocaleChange);
        this.#preview.connect();

        const form = this.$('#page-form');
        // A click on a submit button (Enter in a field clicks it too) makes the browser check the
        // form right after the click; only those checks are a save attempt.
        form.addEventListener('click', (e) => this.#onSubmitClick(e), true);
        // `invalid` does not bubble, so the form listens in the capture phase.
        form.addEventListener('invalid', (e) => this.#onInvalid(e), true);
        form.addEventListener('input', (e) => this.#clearFieldError(e.target));
        form.addEventListener('change', (e) => this.#clearFieldError(e.target));

        form.addEventListener('submit', (e) => {
            e.preventDefault();
            if (this.#awaitingImages) return;
            // Only the publish button asks for the saved page to go live; Enter in a field
            // clicks the first submit button, which only saves.
            const publish = e.submitter?.id === 'btn-submit-publish';
            // A photo chosen just before saving is still being prepared: save once it is.
            if (!imagePreparationsSettled()) {
                this.#submit(form, publish);
                return;
            }
            this.#awaitingImages = true;
            this.loadingMode = publish ? 'publish' : 'submit';
            this.loading = true;
            this.#awaitImagePreparations().then(() => {
                this.#awaitingImages = false;
                this.loading = false;
                this.#submit(form, publish);
            });
        });

        this.$('#btn-cancel')?.addEventListener('click', () => {
            location.hash = this.#editMode ? '#/pages' : '#/';
        });
        this.#preview.start();
    }

    /**
     * Waits until no photo is being prepared any more, including photos chosen while it waits,
     * so the save never sends an original.
     * @returns {Promise<void>}
     */
    async #awaitImagePreparations() {
        for (let preparing = imagePreparationsSettled(); preparing; preparing = imagePreparationsSettled()) {
            await preparing;
        }
    }

    /**
     * Checks the sections and tables, then asks for the page to be saved, and published too when
     * `publish` is set.
     * @param {HTMLFormElement} form
     * @param {boolean} [publish]
     */
    #submit(form, publish = false) {
        this.submitError = null;
        if (this.#sectionEditor && !this.#sectionEditor.validate({focus: true})) {
            this.submitError = this.#sectionEditor.compatible
                ? t('pageForm.compositionInvalid')
                : t('pageForm.compositionIncompatible');
            return;
        }
        const tableProblem = this.#firstTableProblem();
        if (tableProblem) {
            tableProblem.focus();
            this.submitError = tableProblem.message;
            return;
        }
        this.emit('page-submit', {
            templateName: this.#templateName,
            formElement: form,
            fields: this.#fields,
            editMode: this.#editMode,
            pagePath: this.#pagePath,
            publish,
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('locale-change', this.#onLocaleChange);
        if (this.#formListenerController) {
            this.#formListenerController.abort();
            this.#formListenerController = null;
        }
        this.#preview.disconnect();
        this.#layout.dispose();
    }

    /**
     * The browser found a field it will not submit (empty while required, or out of range).
     * Instead of its own bubble, which cannot point at a field inside a closed editor group or
     * a collapsed image card, the field gets a message under it; the first one of a save
     * attempt is revealed, scrolled to and focused, and the banner says fields need attention.
     * @param {Event} event
     */
    #onInvalid(event) {
        // A control that checks itself (such as the carousel position, with `reportValidity`)
        // keeps the browser's own bubble next to it.
        if (!this.#saveAttempt) return;
        event.preventDefault();
        const input = event.target;
        const group = input.closest('.field-group');
        if (group) {
            let error = group.querySelector(':scope .field-error');
            if (!error) {
                error = document.createElement('p');
                error.className = 'field-error';
                error.id = `field-error-${input.name}`;
                group.appendChild(error);
                const ids = input.getAttribute('aria-describedby');
                input.setAttribute('aria-describedby', ids ? `${ids} ${error.id}` : error.id);
            }
            error.textContent = t(input.validity.valueMissing ? 'pageForm.fieldRequired' : 'pageForm.fieldInvalid');
            group.classList.add('has-error');
        }
        input.setAttribute('aria-invalid', 'true');
        if (this.#revealedInvalid) return;
        this.#revealedInvalid = true;
        // One save attempt fires `invalid` for every bad field in turn, with microtasks run in
        // between; only the first is revealed, so the flag lasts until the next task.
        setTimeout(() => { this.#revealedInvalid = false; }, 0);
        this.#showSubmitError(t('pageForm.fieldsNeedAttention'), {scroll: false});
        for (let details = input.closest('details'); details; details = details.parentElement?.closest('details')) {
            details.open = true;
        }
        const cardHeader = group?.querySelector('.image-card-header[aria-expanded="false"]');
        cardHeader?.setAttribute('aria-expanded', 'true');
        (group ?? input).scrollIntoView({block: 'center'});
        input.focus({preventScroll: true});
    }

    /**
     * Marks the checks the browser runs after a click on a submit button as a save attempt,
     * until the next task.
     * @param {MouseEvent} event
     */
    #onSubmitClick(event) {
        const button = event.target instanceof Element ? event.target.closest('button, input') : null;
        if (!button || button.type !== 'submit' || button.form !== event.currentTarget) return;
        this.#saveAttempt = true;
        setTimeout(() => { this.#saveAttempt = false; }, 0);
    }

    /**
     * Removes a field's error message once its value is acceptable.
     * @param {EventTarget} target
     */
    #clearFieldError(target) {
        if (!(target instanceof HTMLElement) || target.getAttribute('aria-invalid') !== 'true' || !target.validity?.valid) return;
        target.removeAttribute('aria-invalid');
        const group = target.closest('.field-group');
        group?.classList.remove('has-error');
        group?.querySelector('.field-error')?.remove();
        const ids = (target.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== `field-error-${target.name}`);
        if (ids.length > 0) {
            target.setAttribute('aria-describedby', ids.join(' '));
        } else {
            target.removeAttribute('aria-describedby');
        }
    }

    /**
     * Whether the form holds changes that are not saved: edits to an opened page, or anything
     * entered into a new one.
     * @returns {boolean}
     */
    get hasUnsavedChanges() {
        return this.#hasChanges;
    }

    /** Takes the current values as the saved state, after a successful save. */
    markSaved() {
        this.#baselineSnapshot = this.#formSnapshot();
        this.#hasChanges = false;
        this.#syncStatusPill();
        this.#syncSubmitAvailability();
    }

    /** @param {boolean} value */
    set loading(value) {
        const btn = this.$('#btn-submit');
        const overlay = this.$('#loading-overlay');
        const loadingText = this.$('#loading-text');

        this.#loading = value;
        // A finished page or template load is the state the user starts from.
        if (!value && this.#loadingMode === 'loading') {
            this.markSaved();
        }
        this.#syncSubmitAvailability();
        if (btn) {
            if (!value) {
                this.#setSubmitButtonLabel(this.#editMode ? t('pageForm.actionStageEdit') : t('pageForm.actionStageDraft'));
                this.#setButtonLabel(this.$('#btn-submit-publish'), t('pageForm.actionPublish'));
            } else if (this.#loadingMode === 'loading') {
                this.#setSubmitButtonLabel(t('pageForm.loadingData'));
            } else if (this.#loadingMode === 'publish') {
                // The clicked publish button says what runs; the save button keeps its own label.
                this.#setButtonLabel(this.$('#btn-submit-publish'), t('pageForm.loadingOverlay'));
            } else if (this.#editMode) {
                this.#setSubmitButtonLabel(t('pageForm.loadingSubmitEdit'));
            } else {
                this.#setSubmitButtonLabel(t('pageForm.loadingSubmitDraft'));
            }
        }

        if (overlay) {
            overlay.hidden = !value;
        }

        if (loadingText) {
            if (this.#loadingMode === 'loading') {
                loadingText.textContent = t('pageForm.overlayLoadingData');
            } else if (this.#loadingMode === 'publish') {
                loadingText.textContent = t('pageForm.loadingOverlay');
            } else {
                loadingText.textContent = this.#editMode ? t('pageForm.overlayStageEdit') : t('pageForm.overlaySavingDraft');
            }
        }

        const cancelBtn = this.$('#btn-cancel');
        if (cancelBtn) {
            cancelBtn.disabled = value;
        }
    }

    /** @param {string|null} message */
    set submitError(message) {
        this.#showSubmitError(message, {scroll: true});
    }

    /**
     * Shows or clears the error banner above the form. The banner is an alert region, so it is
     * made visible before its text is set, which is what assistive technology announces.
     * @param {string|null} message
     * @param {{scroll: boolean}} options `scroll` brings the banner into view
     */
    #showSubmitError(message, {scroll}) {
        const banner = this.$('#submit-error');
        if (!banner) return;
        if (message) {
            banner.hidden = false;
            banner.textContent = message;
            if (scroll) banner.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        } else {
            banner.hidden = true;
            banner.textContent = '';
        }
    }

    /** @param {'submit'|'loading'|'publish'} value */
    set loadingMode(value) {
        this.#loadingMode = value || 'submit';
    }

    /** @param {boolean} value */
    set editMode(value) {
        this.#editMode = value;
        this.#hasChanges = false;
        this.#baselineSnapshot = null;
        this.#syncHeading();
        this.#updateSlugPreview();
        const btn = this.$('#btn-submit');
        if (btn) {
            this.#setSubmitButtonLabel(value ? t('pageForm.actionStageEdit') : t('pageForm.actionStageDraft'));
        }
        const backLink = this.$('#back-link');
        if (backLink) {
            backLink.href = value ? '#/pages' : '#/';
        }
        this.#syncStatusPill();
        this.#syncSubmitAvailability();
        this.#preview.reset();
    }

    /**
     * Saving is only offered while it would store something: never during a request, and in
     * edit mode only once the form differs from the opened page. Saving and publishing is also
     * offered for an unchanged draft or an unchanged page with a staged edit, which it publishes;
     * only a live page with nothing changed and nothing staged has nothing to publish. The
     * tooltips say why a button is unavailable.
     */
    #syncSubmitAvailability() {
        const unchanged = this.#editMode && !this.#hasChanges;
        this.#syncButtonAvailability(this.$('#btn-submit'), unchanged);
        // A placement over a pinned page is saved, but a publication refuses it.
        const publishBlock = this.#layout.overlapsPinnedPage() ? 'pageForm.publishBlockedByPinned' : null;
        this.#syncButtonAvailability(this.$('#btn-submit-publish'), unchanged && this.#pageLive && !this.#pagePending, publishBlock);
    }

    /**
     * @param {HTMLButtonElement|null} btn
     * @param {boolean} unchanged whether the button has nothing to store
     * @param {string|null} [blockKey] the translation key of why the button may not be used, if it may not
     */
    #syncButtonAvailability(btn, unchanged, blockKey = null) {
        if (!btn) return;
        btn.disabled = this.#loading || unchanged || blockKey !== null;
        if (this.#loading) {
            btn.removeAttribute('title');
        } else if (blockKey !== null) {
            btn.title = t(blockKey);
        } else if (unchanged) {
            btn.title = t('pageForm.noChanges');
        } else {
            btn.removeAttribute('title');
        }
    }

    /**
     * Mirrors the form's current lifecycle state on the persistent status pill at the
     * top of the page. Three states: "new draft" (Concept), "live page" (an existing
     * page opened for editing, nothing changed and nothing waiting for publication) and
     * "editing an existing live page" (Concept wijzigt live pagina, once the user changes
     * a field, or from the start when the page was opened with a staged edit that is not
     * published yet). Only the pill reflects that staged edit: unsaved-changes tracking
     * and the Save button still look at local changes alone.
     */
    #syncStatusPill() {
        const pill = this.$('#status-pill');
        const label = this.$('#status-pill-label');
        const help = this.$('#status-pill-help');
        if (!pill || !label) return;
        const state = !this.#editMode || !this.#pageLive ? 'draft' : this.#hasChanges || this.#pagePending ? 'editing' : 'live';
        pill.classList.toggle('status-pill--draft', state === 'draft');
        pill.classList.toggle('status-pill--live', state === 'live');
        pill.classList.toggle('status-pill--editing', state === 'editing');
        const [labelKey, helpKey] = {
            draft: ['pageForm.statusNewDraft', 'pageForm.statusHelp'],
            live: ['pageForm.statusLive', 'pageForm.statusLiveHelp'],
            editing: ['pageForm.statusEditingLive', 'pageForm.statusEditingHelp'],
        }[state];
        label.textContent = t(labelKey);
        if (help) help.textContent = t(helpKey);
        const icon = pill.querySelector('.status-pill-icon');
        if (icon) {
            icon.classList.toggle('fa-globe', state === 'live');
            icon.classList.toggle('fa-floppy-disk', state !== 'live');
        }
    }

    /** @param {boolean} value */
    set pagePending(value) {
        this.#pagePending = value === true;
        this.#syncStatusPill();
        this.#syncSubmitAvailability();
    }

    /**
     * Heading states what the form does: "New <template>" when creating, "Edit <page>"
     * when editing, plus the page's public URL in edit mode (linked only once it is live).
     */
    #syncHeading() {
        const heading = this.$('#form-heading');
        if (heading) {
            const name = this.#editMode
                ? (this.#pageTitle || displayName(this.#templateName))
                : displayName(this.#templateName);
            heading.textContent = tpl(this.#editMode ? 'pageForm.headingEdit' : 'pageForm.headingCreate', {name});
        }
        const line = this.$('#page-url-line');
        const link = this.$('#page-url');
        if (!line || !link) return;
        const showUrl = this.#editMode && Boolean(this.#pagePath) && this.#mode !== RenderMode.INDEX_FRAGMENT;
        line.hidden = !showUrl;
        if (!showUrl) return;
        link.textContent = pageUrlPath(this.#pagePath);
        const href = this.#pageLive ? liveUrl(this.#domain, this.#pagePath) : null;
        if (href) {
            link.href = href;
            link.target = '_blank';
        } else {
            link.removeAttribute('href');
            link.removeAttribute('target');
        }
    }

    /** Compare the values that would be saved, including nested rows and pending uploads. */
    #formSnapshot() {
        const form = this.$('#page-form');
        if (!form) return [];
        const values = [...toPublishFormData(this.#fields, form, this.#templateName, this.#layoutConfig).entries()];
        for (const input of form.querySelectorAll('input[type="file"][name]')) {
            values.push([`existing:${input.name}`, input.dataset.existingPath || '']);
        }
        return values;
    }

    /** Keep the live-page pill in sync even when a user undoes a change. */
    #markChanged() {
        const current = this.#formSnapshot();
        const baseline = this.#baselineSnapshot ?? [];
        const changed = current.length !== baseline.length || current.some(([key, value], index) =>
            key !== baseline[index][0] || value !== baseline[index][1]);
        if (changed !== this.#hasChanges) {
            this.#hasChanges = changed;
            this.#syncStatusPill();
            this.#syncSubmitAvailability();
        }
    }

    /** @param {boolean} live */
    set pageLive(live) {
        this.#pageLive = live;
        this.#syncHeading();
        this.#syncStatusPill();
        this.#syncSubmitAvailability();
        this.#updateSlugPreview();
    }

    /** @param {string} path */
    set pagePath(path) {
        this.#pagePath = path;
        this.#syncHeading();
        this.#updateSlugPreview();
        this.#layout.clearPreviewState();
        this.#layout.renderMaps();
    }

    /**
     * Updates the submit button's label text without clobbering the leading icon.
     * @param {string} label
     */
    #setSubmitButtonLabel(label) {
        this.#setButtonLabel(this.$('#btn-submit'), label);
    }

    /**
     * Updates a button's label text without clobbering the leading icon.
     * @param {HTMLButtonElement} btn
     * @param {string} label
     */
    #setButtonLabel(btn, label) {
        const labelSpan = btn.querySelector('span');
        if (labelSpan) {
            labelSpan.textContent = label;
        } else {
            btn.textContent = label;
        }
    }

    /** @param {Object<string, *>} data */
    set prefillData(data) {
        if (!data) {
            return;
        }
        this.#hasChanges = false;
        this.#pageTitle = typeof data[PageKey.TITLE] === 'string' ? data[PageKey.TITLE].trim() : '';
        this.#syncHeading();
        this.#syncStatusPill();
        this.#syncSubmitAvailability();
        this.#preview.reset();
        const tableKeys = new Set(this.#fields.filter((field) => field.type === FieldType.TABLE).map((field) => field.key));
        for (const [key, value] of Object.entries(data)) {
            if (key === COMPOSITION_DATA_KEY) {
                continue;
            }
            if (tableKeys.has(key)) {
                this.$(`table-field[name="${key}"]`).rows = value;
                continue;
            }
            const input = this.$(`[name="${key}"]`);
            if (!input) {
                continue;
            }

            if (input.type === 'file') {
                if (value) {
                    showExistingAsset(input, value, (name, detail) => this.emit(name, detail));
                }
                continue;
            }
            if (input.type === 'checkbox') {
                input.checked = isTruthyFlag(value);
                continue;
            }
            if (key.endsWith(ImageCompanionSuffix.POSITION)) {
                const grid = this.$(`.focal-point-grid[data-for="${key}"]`);
                if (grid) markFocalPoint(grid, value);
            }
            input.value = value;
        }
        this.#layout.wrapGridInputsToColumns();
        this.#sectionEditor?.restore(data[COMPOSITION_DATA_KEY], data);
        syncEditorGroupExpansion(this.shadowRoot, this.#fields, data);
        this.#fieldReferences.sync(this.#fields);
        this.#layout.renderMaps();
        this.#layout.syncGridFieldVisibility();
        this.#layout.clearPositionTouched();
        this.#layout.requestPreview();
        this.#evaluateFormReadiness();
        this.#baselineSnapshot = this.#formSnapshot();
        // Programmatic value writes don't fire input events, so the slug
        // preview pill and the character counters need an explicit refresh
        // after edit-mode prefill.
        this.#updateSlugPreview();
        syncFieldCounters(this.shadowRoot);
        // Edit mode prefills a known-good page, so render its preview straight
        // away rather than leaving an empty frame until a manual Refresh click.
        // Publish mode starts blank, so it keeps waiting for user input first.
        if (this.#editMode) {
            this.#preview.request();
        }
    }

    /**
     * Starts loading a template. The section editor is dropped, so the next form render starts
     * from the template defaults instead of carrying items over from the previous page.
     * @param {string} value
     */
    set templateName(value) {
        this.#templateName = value;
        this.#sectionEditor = null;
        this.#pageTitle = '';
        this.#syncHeading();
        this.#layout.clearPreviewState();
        this.#layout.clearPositionTouched();
        this.#preview.reset();
    }

    /** @param {string} value */
    set description(value) {
        const el = this.$('#template-description');
        if (el) {
            el.textContent = value || t('pageForm.defaultDescription');
        }
    }

    /** @param {string} html */
    set previewContent(html) {
        this.#preview.render(html);
    }

    /** @param {boolean} value */
    set previewLoading(value) {
        this.#preview.setLoading(value);
    }

    /**
     * Called when a preview request fails. Keeps the last successful preview on screen and
     * labels it as outdated; edits are untouched.
     * @returns {boolean} whether the failure shows inline (an earlier preview is now labelled outdated)
     */
    previewFailed() {
        return this.#preview.failed();
    }

    /** @param {string|null} value */
    set domain(value) {
        this.#domain = (typeof value === 'string' && value.trim().length > 0) ? value.trim().replace(/\/$/, '') : null;
        this.#updateSlugPreview();
        this.#syncHeading();
    }

    /** @param {string|null} value */
    set slugPrefix(value) {
        this.#slugPrefix = (typeof value === 'string' && value.trim().length > 0) ? value.trim() : null;
        this.#renderFields();
    }

    /** @param {boolean} value Whether the loaded template is the client's single homepage template. */
    set homepage(value) {
        this.#homepage = Boolean(value);
        this.#updateSlugPreview();
    }

    /** @param {string|null} value */
    set content(value) {
        this.#templateContent = typeof value === 'string' ? value : '';
        this.#renderFields();
    }

    /** @param {RenderMode | null} value */
    set mode(value) {
        this.#mode = value === RenderMode.INDEX_FRAGMENT ? RenderMode.INDEX_FRAGMENT : RenderMode.PAGE;
        this.#syncHeading();
        // INDEX_FRAGMENT switches the slug preview to a `fragments/…` path, so
        // refresh the pill to reflect the new mode immediately.
        this.#updateSlugPreview();
    }

    /** @param {import('../../shell/types/user.types.js').TemplateComposition | null} value */
    set composition(value) {
        this.#composition = hasComposition(value) ? value : null;
        this.#renderFields();
    }

    /** @param {{enabledByField?: string}|null} value */
    set layoutConfig(value) {
        this.#layoutConfig = value?.contexts && Object.keys(value.contexts).length > 0 ? value : null;
        this.#layout.clearPreviewState();
        this.#layout.clearPositionTouched();
        this.#renderFields();
        this.#layout.renderMaps();
        this.#layout.syncGridFieldVisibility();
        this.#layout.requestPreview();
    }

    get layoutConfig() {
        return this.#layoutConfig;
    }

    /** @param {import('../../shell/types/user.types.js').TemplateField[]} fields */
    set fields(fields) {
        this.#fields = fields;
        this.#layout.clearPreviewState();
        this.#layout.clearPositionTouched();
        this.#renderFields();
        this.#layout.renderMaps();
        this.#layout.requestPreview();
        this.#preview.reset();
    }

    /** @param {import('../../shell/types/user.types.js').TemplateLayoutEntry[]} entries */
    set layoutEntries(entries) {
        this.setLayoutEntriesForContext(DEFAULT_LAYOUT_CONTEXT, entries);
    }

    /** @param {import('../../shell/types/user.types.js').LayoutPreviewResponse | null} preview */
    set layoutPreview(preview) {
        this.setLayoutPreviewForContext(DEFAULT_LAYOUT_CONTEXT, preview);
    }

    get layoutContexts() {
        return this.#layout.contexts();
    }

    setLayoutEntriesForContext(context, entries) {
        this.#layout.setEntries(context, entries);
    }

    setLayoutPreviewForContext(context, preview) {
        this.#layout.setPreview(context, preview);
    }

    resetLayoutData() {
        this.#layout.resetData();
    }

    #renderFields() {
        const container = this.$('#fields-container');
        if (!container) {
            return;
        }
        container.innerHTML = '';

        const reordered = orderFormFields(this.#fields, this.#templateContent, this.#layoutConfig);

        const renderContext = {
            fieldKeys: this.#fields.map((field) => field.key),
            gridHint: (fieldKey) => this.#layout.gridHint(fieldKey),
            bindLayoutToggle: (input, fieldKey) => this.#layout.bindToggle(input, fieldKey),
            onFocalPointChange: () => {
                this.#markChanged();
                this.#preview.scheduleRefresh();
            },
        };
        this.#layout.beginFieldPlacement();

        for (const field of reordered) {
            const {group, input} = renderFieldGroup(field, renderContext);
            if (!this.#layout.adoptFieldGroup(field, group, input)) {
                container.appendChild(group);
            }
        }
        this.#layout.appendContextCards(container);

        // The title is the slug's fallback source, as in the backend's path derivation.
        bindSlugPreview(this.shadowRoot, () => this.#updateSlugPreview());

        this.#renderSectionEditor(container);
        arrangeFieldSections(container);
        renderEditorGroups(container, this.#fields);
        placeLayoutContextCardsInEditorGroups(container, this.#fields, this.#layout.toggleFieldsByContext());
        this.#sectionEditor?.placeFixedGroups();
        this.#fieldReferences.sync(this.#fields);

        if (this.#formListenerController) {
            this.#formListenerController.abort();
        }
        const formEl = this.$('#page-form');
        if (formEl) {
            this.#formListenerController = new AbortController();
            const handler = (event) => {
                // SectionEditor handles these radio changes itself. Refreshing on the preceding
                // input event would restore the old checked state before change can select it.
                if (event.target?.classList?.contains('choice-input')) return;
                this.#markChanged();
                this.#sectionEditor?.refresh();
                this.#evaluateFormReadiness();
                this.#preview.scheduleRefresh();
            };
            const opts = {signal: this.#formListenerController.signal};
            formEl.addEventListener('input', handler, opts);
            formEl.addEventListener('change', handler, opts);
        }
        this.#evaluateFormReadiness();
        if (!this.#baselineSnapshot) {
            this.#baselineSnapshot = this.#formSnapshot();
        }
    }

    /**
     * Builds the section list for templates with a composition. Rebuilding the form (another
     * setter, or the same template's fields or composition set again) keeps the current
     * arrangement, the item values and the pending item uploads while they still fit the
     * composition, so the user's work is not reset.
     * @param {HTMLElement} container
     */
    #renderSectionEditor(container) {
        if (!this.#composition) {
            this.#sectionEditor = null;
            return;
        }
        const previous = this.#sectionEditor;
        this.#sectionEditor = new SectionEditor({
            root: this.shadowRoot,
            container,
            composition: this.#composition,
            fields: this.#fields,
            state: previous?.state ?? null,
            compatible: previous?.compatible ?? true,
            items: previous?.itemValues ?? null,
            onChange: () => {
                this.#markChanged();
                this.#evaluateFormReadiness();
                this.#preview.scheduleRefresh();
            },
            onAssetRequest: (path, img) => this.emit('asset-request', {path, img}),
        });
        this.#sectionEditor.render();
    }

    /**
     * Toggles `.is-ready` on the submit button based on whether every required
     * field currently has a value. Image fields with `dataset.existingPath`
     * count as filled so edit-mode prefills light up the button immediately.
     */
    #evaluateFormReadiness() {
        const btn = this.$('#btn-submit');
        if (!btn) return;

        const sectionOwned = this.#sectionEditor?.ownedKeys ?? new Set();
        const requiredFields = this.#fields.filter((field) => field?.required && !sectionOwned.has(field.key));
        if (requiredFields.length === 0 && !this.#sectionEditor) {
            btn.classList.remove('is-ready');
            return;
        }

        const allFilled = requiredFields.every((field) => this.#isFieldFilled(field));
        const sectionsReady = this.#sectionEditor ? this.#sectionEditor.isReady() : true;
        btn.classList.toggle('is-ready', allFilled && sectionsReady);
    }

    /**
     * First table that the backend would reject: a required table without rows, a blank cell in
     * a required column, or a number cell that is not a number. The row count of a section-owned
     * table is left to the section editor; its cells are checked like any other table's, because
     * the backend validates every submitted row.
     * @returns {{message: string, focus: () => void} | null}
     */
    #firstTableProblem() {
        const sectionOwned = this.#sectionEditor?.ownedKeys ?? new Set();
        for (const field of this.#fields) {
            if (field.type !== FieldType.TABLE) continue;
            const table = this.$(`table-field[name="${field.key}"]`);
            if (field.required && !sectionOwned.has(field.key) && table.rows.length === 0) {
                return {message: tpl('pageForm.tableRowsRequired', {label: field.label}), focus: () => table.focus()};
            }
            const cell = table.firstInvalidCell();
            if (cell) {
                const key = cell.reason === 'required' ? 'pageForm.tableCellRequired' : 'pageForm.tableCellInvalidNumber';
                return {
                    message: tpl(key, {label: field.label, column: cell.columnLabel, n: cell.rowIndex + 1}),
                    focus: () => table.focusCell(cell.rowId, cell.columnKey),
                };
            }
        }
        return null;
    }

    /** @param {{key: string, type: string}} field */
    #isFieldFilled(field) {
        const input = this.$(`[name="${field.key}"]`);
        if (!input) return false;
        if (field.dependsOn && input.closest('.field-group')?.hidden) return true;
        if (field.type === FieldType.TABLE) {
            return input.rows.length > 0 && input.firstInvalidCell()?.reason !== 'required';
        }
        if (isFileFieldType(field.type)) {
            return (input.files && input.files.length > 0) || Boolean(input.dataset.existingPath);
        }
        if (field.type === FieldType.CHECKBOX) {
            return input.checked === true;
        }
        return String(input.value ?? '').trim().length > 0;
    }

    /**
     * Refreshes the live slug-preview pill and the address-change warning; no-ops before the
     * pill exists. Only a live page with a public address (not a fragment) can move.
     */
    #updateSlugPreview() {
        const hasLiveAddress = this.#editMode && this.#pageLive && this.#mode !== RenderMode.INDEX_FRAGMENT;
        renderSlugPreview(this.shadowRoot, {
            templateName: this.#templateName,
            slugPrefix: this.#slugPrefix,
            domain: this.#domain,
            mode: this.#mode,
            homepage: this.#homepage,
            livePath: hasLiveAddress ? this.#pagePath : null,
        });
    }
}

customElements.define('page-form', PageFormComponent);
